import { supabase } from '../lib/supabase';

/**
 * Authentication service for Abidjan agents
 * Handles login for AGENT_ABJ role with restricted access
 */
export const agentAuthService = {
  /**
   * Sign in agent with email and password
   * Validates that user has AGENT_ABJ role
   */
  async signIn(email, password) {
    try {
      // Step 1: Authenticate with Supabase Auth
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });

      if (error) {
        return {
          success: false,
          error: 'Identifiants invalides. Vérifiez votre email et mot de passe.'
        };
      }

      // Step 2: Try to find employee by auth_user_id first (preferred method)
      let { data: employee, error: employeeError } = await supabase
        ?.from('employees')
        ?.select('id, name, email, role, zone, is_active, auth_user_id')
        ?.eq('auth_user_id', data?.user?.id)
        ?.eq('role', 'AGENT_ABJ')
        ?.single();

      // Step 3: Fallback - If not found by auth_user_id, try by email
      if (employeeError || !employee) {
        const { data: employeeByEmail, error: emailError } = await supabase
          ?.from('employees')
          ?.select('id, name, email, role, zone, is_active, auth_user_id')
          ?.eq('email', data?.user?.email)
          ?.eq('role', 'AGENT_ABJ')
          ?.single();

        if (emailError || !employeeByEmail) {
          await supabase?.auth?.signOut();
          return {
            success: false,
            error: 'Aucun profil agent trouvé pour cet email. Contactez l\'administration.'
          };
        }

        // Step 4: Auto-link - Update auth_user_id if found by email
        if (!employeeByEmail?.auth_user_id) {
          const { error: updateError } = await supabase
            ?.from('employees')
            ?.update({ auth_user_id: data?.user?.id })
            ?.eq('id', employeeByEmail?.id);

          if (updateError) {
            console.error('Failed to auto-link employee to auth user:', updateError);
          }
        }

        employee = employeeByEmail;
      }

      // Step 5: Check if employee is active
      if (!employee?.is_active) {
        await supabase?.auth?.signOut();
        return {
          success: false,
          error: 'Votre compte agent est désactivé. Contactez l\'administration pour réactiver votre compte.'
        };
      }

      return {
        success: true,
        user: data?.user,
        employee: {
          id: employee?.id,
          name: employee?.name,
          email: employee?.email,
          role: employee?.role,
          zone: employee?.zone,
          isActive: employee?.is_active
        }
      };
    } catch (err) {
      console.error('Agent signIn error:', err);
      return {
        success: false,
        error: 'Erreur de connexion. Veuillez réessayer.'
      };
    }
  },

  /**
   * Sign out current agent
   */
  async signOut() {
    const { error } = await supabase?.auth?.signOut();
    if (error) throw error;
  },

  /**
   * Get current agent session
   */
  async getSession() {
    try {
      const { data: { session }, error } = await supabase?.auth?.getSession();
      if (error) throw error;

      if (!session) return null;

      // Get agent employee data
      const { data: employee, error: employeeError } = await supabase
        ?.from('employees')
        ?.select('id, name, email, role, zone, is_active')
        ?.eq('auth_user_id', session?.user?.id)
        ?.eq('role', 'AGENT_ABJ')
        ?.single();

      if (employeeError || !employee) return null;

      return {
        user: session?.user,
        employee: {
          id: employee?.id,
          name: employee?.name,
          email: employee?.email,
          role: employee?.role,
          zone: employee?.zone,
          isActive: employee?.is_active
        }
      };
    } catch (error) {
      console.error('Error getting agent session:', error);
      return null;
    }
  },

  /**
   * Get agent profile data
   */
  async getAgentProfile(userId) {
    try {
      const { data, error } = await supabase
        ?.from('employees')
        ?.select('id, name, email, role, zone, phone, is_active, notes')
        ?.eq('auth_user_id', userId)
        ?.eq('role', 'AGENT_ABJ')
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        email: data?.email,
        role: data?.role,
        zone: data?.zone,
        phone: data?.phone,
        isActive: data?.is_active,
        notes: data?.notes
      };
    } catch (error) {
      throw error;
    }
  }
};